import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PlayCircle, ChevronUp, ArrowRight } from "lucide-react";

/* A small simulated Telegram chat used to show users exactly what @BotFather and
   @userinfobot say — so they never get stuck at the "create your own bot" step. */

const Bubble = ({ side = "in", children, i = 0, highlight = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.12 * i, duration: 0.28 }}
    className={`flex ${side === "out" ? "justify-end" : "justify-start"}`}
  >
    <div
      className={[
        "max-w-[82%] px-3 py-2 rounded-2xl text-[12.5px] leading-[1.45] whitespace-pre-line",
        side === "out"
          ? "bg-[#2b5278] text-white rounded-br-md"
          : "bg-[#182533] text-[#e7eef5] rounded-bl-md",
        highlight ? "ring-2 ring-[#0ea5e9]" : "",
      ].join(" ")}
    >
      {children}
    </div>
  </motion.div>
);

const Phone = ({ title, children }) => (
  <div className="rounded-2xl overflow-hidden border border-[#243447] bg-[#0e1621] shadow-lg">
    <div className="bg-[#17212b] px-3.5 py-2.5 flex items-center gap-2 border-b border-[#243447]">
      <div className="w-7 h-7 rounded-full bg-[#0ea5e9]/20 flex items-center justify-center text-[#3aa0e3] text-xs font-bold">
        {title.charAt(0).toUpperCase()}
      </div>
      <div>
        <p className="text-white text-[13px] font-semibold leading-none">{title}</p>
        <p className="text-[#6b8096] text-[10px] mt-0.5">bot</p>
      </div>
    </div>
    <div className="p-3 space-y-2 bg-[#0e1621]">{children}</div>
  </div>
);

const Tag = ({ children }) => (
  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0ea5e9] bg-[#0ea5e9]/10 px-2 py-1 rounded-md">
    <ArrowRight size={12} /> {children}
  </span>
);

export default function TelegramGuide() {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-4" data-testid="telegram-guide">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="telegram-guide-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-center gap-2 text-[13px] font-semibold text-[#0ea5e9] bg-[#0ea5e9]/10 hover:bg-[#0ea5e9]/15 transition-colors py-2.5 rounded-xl"
      >
        {open ? <ChevronUp size={16} /> : <PlayCircle size={16} />}
        {open ? "Hide walkthrough" : "Show me how (1 min)"}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-4 space-y-5">
              {/* Step 1 — create the bot */}
              <div>
                <p className="text-[13px] font-bold text-white mb-2">1 · Create your bot in @BotFather</p>
                <Phone title="BotFather">
                  <Bubble side="out" i={0}>/newbot</Bubble>
                  <Bubble side="in" i={1}>Alright, a new bot. How are we going to call it? Please choose a name.</Bubble>
                  <Bubble side="out" i={2}>My Tennis Alerts</Bubble>
                  <Bubble side="in" i={3}>Good. Now let's choose a username. It must end in `bot`.</Bubble>
                  <Bubble side="out" i={4}>my_tennis_alerts_bot</Bubble>
                  <Bubble side="in" i={5} highlight>
                    {"Done! Congratulations on your new bot.\n\nUse this token to access the HTTP API:\n7654321:AAH-XK9d...your_token_here"}
                  </Bubble>
                </Phone>
                <div className="mt-2"><Tag>Copy that token → paste as Bot Token</Tag></div>
              </div>

              {/* Step 2 — start your bot */}
              <div>
                <p className="text-[13px] font-bold text-white mb-2">2 · Open your new bot &amp; tap Start</p>
                <Phone title="My Tennis Alerts">
                  <Bubble side="in" i={0}>This bot can&apos;t talk to you until you press Start.</Bubble>
                  <Bubble side="out" i={1}>/start</Bubble>
                </Phone>
                <p className="text-[11.5px] text-[#9ca3af] mt-2">This lets your bot send messages to you.</p>
              </div>

              {/* Step 3 — get chat id */}
              <div>
                <p className="text-[13px] font-bold text-white mb-2">3 · Get your Chat ID from @userinfobot</p>
                <Phone title="userinfobot">
                  <Bubble side="out" i={0}>/start</Bubble>
                  <Bubble side="in" i={1} highlight>
                    {"Id: 123456789\nFirst: Alex\nLang: en"}
                  </Bubble>
                </Phone>
                <div className="mt-2"><Tag>Copy the Id → paste as Chat ID</Tag></div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
